import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  CalendarCheck,
  Scissors,
  Settings as SettingsIcon,
  Ban,
  LogOut,
  Plus,
  Trash2,
  Edit2,
  X,
  Loader2,
  Mail,
  Phone,
  Dog,
  Check,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api, apiError } from "../lib/api";
import { toast, Toaster } from "sonner";
import { LOGO_URL } from "../lib/data";

const TABS = [
  { id: "bookings", label: "Bookings", icon: CalendarCheck },
  { id: "services", label: "Services", icon: Scissors },
  { id: "blocked", label: "Blocked Times", icon: Ban },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState("bookings");

  if (!ready) return <div className="min-h-screen bg-cream" />;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-cream relative">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#F9F6F0",
            color: "#4A3525",
            border: "1px solid #E6DEC3",
            borderRadius: "16px",
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-[#E6DEC3]/60 bg-cream/90 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <img src={LOGO_URL} alt="Barks & Co" className="h-10 w-10 rounded-full" />
            <div className="leading-tight">
              <div className="font-serif text-lg text-brown-dark">Barks &amp; Co</div>
              <div className="eyebrow" style={{ fontSize: "0.55rem" }}>Studio Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-text-secondary hidden sm:inline">{user.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-brown-warm hover:text-lilac-dark transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === id
                  ? "border-brown-dark text-brown-dark"
                  : "border-transparent text-brown-warm hover:text-brown-dark"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {tab === "bookings" && <BookingsTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "blocked" && <BlockedTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

// ----------------- BOOKINGS TAB -----------------
function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings");
      setBookings(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const now = new Date().toISOString();
    if (filter === "upcoming") return bookings.filter((b) => b.start >= now && b.status === "confirmed");
    if (filter === "past") return bookings.filter((b) => b.start < now);
    if (filter === "cancelled") return bookings.filter((b) => b.status === "cancelled");
    return bookings;
  }, [bookings, filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Mark this booking as cancelled?")) return;
    try {
      await api.patch(`/admin/bookings/${id}`, { status: "cancelled" });
      toast.success("Booking cancelled.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-bookings-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl text-brown-dark">Bookings</h1>
          <p className="text-text-secondary text-sm mt-1">
            {filtered.length} {filter} booking{filtered.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex gap-2 bg-cream-alt rounded-full p-1 border border-[#E6DEC3]" data-testid="bookings-filter">
          {["upcoming", "past", "cancelled", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                filter === f ? "bg-brown-dark text-cream" : "text-brown-warm hover:text-brown-dark"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : filtered.length === 0 ? (
        <Empty message={`No ${filter} bookings.`} />
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {filtered.map((b) => (
            <BookingRow key={b.id} b={b} onCancel={() => cancel(b.id)} onDelete={() => remove(b.id)} onEdit={() => setEditing(b)} />
          ))}
        </div>
      )}

      {editing && <EditBookingModal booking={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BookingRow({ b, onCancel, onDelete, onEdit }) {
  const start = new Date(b.start);
  const when = start.toLocaleString("en-NZ", {
    weekday: "short", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Pacific/Auckland",
  });
  return (
    <div
      data-testid={`booking-row-${b.id}`}
      className={`bg-cream-alt border rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 ${
        b.status === "cancelled" ? "border-[#E6DEC3] opacity-60" : "border-[#E6DEC3] hover:border-lilac"
      } transition-colors`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="eyebrow !text-brown-warm">{when}</span>
          {b.status === "cancelled" && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-brown-warm/15 text-brown-warm">Cancelled</span>
          )}
        </div>
        <div className="font-serif text-xl text-brown-dark">
          {b.service_name}{b.size ? ` · ${b.size}` : ""} · NZ${b.price}
        </div>
        <div className="text-sm text-text-secondary mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
          <span className="flex items-center gap-1.5"><Dog size={13} /> {b.dog_name}{b.dog_breed ? ` (${b.dog_breed})` : ""}</span>
          <span className="flex items-center gap-1.5">{b.customer_name}</span>
          <a href={`tel:${b.customer_phone}`} className="flex items-center gap-1.5 hover:text-lilac-dark"><Phone size={13} /> {b.customer_phone}</a>
          <a href={`mailto:${b.customer_email}`} className="flex items-center gap-1.5 hover:text-lilac-dark"><Mail size={13} /> {b.customer_email}</a>
        </div>
        {b.notes && (
          <div className="text-xs text-brown-warm mt-2 italic">"{b.notes}"</div>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <IconBtn onClick={onEdit} testId={`edit-${b.id}`} title="Edit"><Edit2 size={14} /></IconBtn>
        {b.status !== "cancelled" && (
          <IconBtn onClick={onCancel} testId={`cancel-${b.id}`} title="Cancel"><X size={14} /></IconBtn>
        )}
        <IconBtn onClick={onDelete} testId={`delete-${b.id}`} title="Delete" danger><Trash2 size={14} /></IconBtn>
      </div>
    </div>
  );
}

function EditBookingModal({ booking, onClose, onSaved }) {
  const [b, setB] = useState({
    customer_name: booking.customer_name,
    customer_email: booking.customer_email,
    customer_phone: booking.customer_phone,
    dog_name: booking.dog_name,
    notes: booking.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${booking.id}`, b);
      toast.success("Booking updated.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <ModalShell onClose={onClose} title="Edit booking">
      <div className="grid sm:grid-cols-2 gap-4">
        <ModalInput label="Customer name" value={b.customer_name} onChange={(v) => setB({ ...b, customer_name: v })} />
        <ModalInput label="Phone" value={b.customer_phone} onChange={(v) => setB({ ...b, customer_phone: v })} />
        <ModalInput label="Email" value={b.customer_email} onChange={(v) => setB({ ...b, customer_email: v })} />
        <ModalInput label="Dog's name" value={b.dog_name} onChange={(v) => setB({ ...b, dog_name: v })} />
      </div>
      <div className="mt-4">
        <label className="block eyebrow mb-2">Notes</label>
        <textarea value={b.notes} onChange={(e) => setB({ ...b, notes: e.target.value })} rows={3}
          className="w-full bg-cream-alt border border-[#E6DEC3] rounded-2xl px-4 py-3 text-brown-dark focus:outline-none focus:ring-1 focus:ring-lilac focus:border-lilac" />
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={save} disabled={saving} data-testid="modal-save" className="btn-primary disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save
        </button>
        <button onClick={onClose} className="btn-outline">Cancel</button>
      </div>
    </ModalShell>
  );
}

// ----------------- SERVICES TAB -----------------
function ServicesTab() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/services");
      setServices(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      toast.success("Service deleted.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-services-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl text-brown-dark">Services</h1>
          <p className="text-text-secondary text-sm mt-1">Edit your menu, pricing and durations.</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary" data-testid="add-service-button">
          <Plus size={14} /> Add Service
        </button>
      </div>
      {loading ? <Spinner /> : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="services-list">
          {services.map((s) => (
            <div key={s.id} data-testid={`service-card-${s.id}`}
              className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-serif text-xl text-brown-dark">{s.name}</h3>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  s.active ? "bg-lilac-soft text-brown-dark" : "bg-brown-warm/15 text-brown-warm"
                }`}>{s.active ? "Active" : "Hidden"}</span>
              </div>
              <p className="text-sm text-text-secondary leading-relaxed">{s.description}</p>
              <div className="flex items-center justify-between mt-3 text-xs">
                <span className="text-brown-warm">{s.duration_minutes} min</span>
                <span className="text-brown-dark">
                  {s.prices.default != null ? `NZ$${s.prices.default}` : Object.entries(s.prices).map(([k, v]) => `${k}: $${v}`).join(" · ")}
                </span>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(s)} data-testid={`edit-service-${s.id}`}
                  className="text-xs text-brown-dark hover:text-lilac-dark inline-flex items-center gap-1">
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`}
                  className="text-xs text-brown-warm hover:text-red-700 inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(editing || creating) && (
        <ServiceModal
          service={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function ServiceModal({ service, onClose, onSaved }) {
  const isEdit = !!service;
  const [hasSizes, setHasSizes] = useState(!!(service && service.prices && !("default" in service.prices)));
  const [form, setForm] = useState(() => ({
    name: service?.name || "",
    description: service?.description || "",
    duration_minutes: service?.duration_minutes || 60,
    active: service?.active ?? true,
    sort_order: service?.sort_order || 99,
    prices: service?.prices || { default: 0 },
  }));
  const [saving, setSaving] = useState(false);

  const togglePricing = () => {
    if (hasSizes) {
      setHasSizes(false);
      const first = Object.values(form.prices)[0] || 0;
      setForm({ ...form, prices: { default: first } });
    } else {
      setHasSizes(true);
      const v = form.prices.default || 0;
      setForm({ ...form, prices: { small: v, medium: v, large: v } });
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/admin/services/${service.id}`, form);
      else        await api.post(`/admin/services`, form);
      toast.success(isEdit ? "Service updated." : "Service added.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title={isEdit ? "Edit service" : "Add service"}>
      <ModalInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="service-name" />
      <div className="mt-4">
        <label className="block eyebrow mb-2">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          data-testid="service-description"
          className="w-full bg-cream-alt border border-[#E6DEC3] rounded-2xl px-4 py-3 text-brown-dark focus:outline-none focus:ring-1 focus:ring-lilac focus:border-lilac"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <ModalInput label="Duration (min)" type="number" value={form.duration_minutes} onChange={(v) => setForm({ ...form, duration_minutes: Number(v) })} testId="service-duration" />
        <ModalInput label="Sort order" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) })} testId="service-sort" />
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <label className="eyebrow">Pricing</label>
          <button onClick={togglePricing} data-testid="toggle-pricing"
            className="text-xs text-brown-warm underline hover:text-lilac-dark">
            {hasSizes ? "Switch to single price" : "Switch to per-size pricing"}
          </button>
        </div>
        {hasSizes ? (
          <div className="grid grid-cols-3 gap-3">
            {["small", "medium", "large"].map((sz) => (
              <ModalInput key={sz} label={sz} type="number" value={form.prices[sz] ?? 0}
                onChange={(v) => setForm({ ...form, prices: { ...form.prices, [sz]: Number(v) } })}
                testId={`service-price-${sz}`} />
            ))}
          </div>
        ) : (
          <ModalInput label="Price (NZ$)" type="number" value={form.prices.default ?? 0}
            onChange={(v) => setForm({ ...form, prices: { default: Number(v) } })}
            testId="service-price-default" />
        )}
      </div>

      <label className="flex items-center gap-3 mt-5 cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="service-active" />
        <span className="text-sm text-brown-dark">Show on public booking page</span>
      </label>

      <div className="flex gap-3 mt-6">
        <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="service-save">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save
        </button>
        <button onClick={onClose} className="btn-outline">Cancel</button>
      </div>
    </ModalShell>
  );
}

// ----------------- BLOCKED TAB -----------------
function BlockedTab() {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start: "", end: "", reason: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blocked-slots");
      setBlocked(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.start || !form.end) return toast.error("Start and end required.");
    setCreating(true);
    try {
      await api.post("/admin/blocked-slots", {
        start: new Date(form.start).toISOString(),
        end: new Date(form.end).toISOString(),
        reason: form.reason,
      });
      toast.success("Time blocked.");
      setForm({ start: "", end: "", reason: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blocked-slots/${id}`);
      toast.success("Block removed.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const localInput = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short", timeZone: "Pacific/Auckland" });
  };

  return (
    <div data-testid="admin-blocked-tab">
      <div className="mb-6">
        <h1 className="font-serif text-4xl text-brown-dark">Blocked Times</h1>
        <p className="text-text-secondary text-sm mt-1">Block out holidays, personal time, or one-off closures.</p>
      </div>

      <form onSubmit={create} className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-5 mb-6 grid sm:grid-cols-4 gap-3" data-testid="blocked-form">
        <ModalInput label="Start" type="datetime-local" value={form.start} onChange={(v) => setForm({ ...form, start: v })} testId="blocked-start" />
        <ModalInput label="End" type="datetime-local" value={form.end} onChange={(v) => setForm({ ...form, end: v })} testId="blocked-end" />
        <ModalInput label="Reason" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} testId="blocked-reason" />
        <button type="submit" disabled={creating} className="btn-primary self-end disabled:opacity-60" data-testid="blocked-submit">
          {creating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Block
        </button>
      </form>

      {loading ? <Spinner /> : blocked.length === 0 ? (
        <Empty message="No blocked times yet." />
      ) : (
        <div className="space-y-2" data-testid="blocked-list">
          {blocked.map((b) => (
            <div key={b.id} data-testid={`blocked-row-${b.id}`}
              className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="text-brown-dark font-medium">
                  {localInput(b.start)} → {localInput(b.end)}
                </div>
                {b.reason && <div className="text-xs text-brown-warm mt-1">{b.reason}</div>}
              </div>
              <button onClick={() => remove(b.id)} data-testid={`remove-block-${b.id}`}
                className="text-brown-warm hover:text-red-700">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------- SETTINGS TAB -----------------
const DAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/settings");
      setSettings(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const updateDay = (key, patch) =>
    setSettings({ ...settings, working_hours: { ...settings.working_hours, [key]: { ...settings.working_hours[key], ...patch } } });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/settings", settings);
      toast.success("Settings saved.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!settings) return <Spinner />;

  return (
    <div data-testid="admin-settings-tab">
      <div className="mb-6">
        <h1 className="font-serif text-4xl text-brown-dark">Settings</h1>
        <p className="text-text-secondary text-sm mt-1">Working hours and booking rules.</p>
      </div>

      <div className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-6 mb-6">
        <h3 className="font-serif text-xl text-brown-dark mb-4">Booking rules</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <ModalInput label="Slot step (minutes)" type="number" value={settings.slot_step_minutes}
            onChange={(v) => setSettings({ ...settings, slot_step_minutes: Number(v) })}
            testId="settings-slot-step" />
          <ModalInput label="Min lead time (hours)" type="number" value={settings.booking_buffer_hours}
            onChange={(v) => setSettings({ ...settings, booking_buffer_hours: Number(v) })}
            testId="settings-buffer" />
        </div>
      </div>

      <div className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-6 mb-6">
        <h3 className="font-serif text-xl text-brown-dark mb-4">Working hours (NZ time)</h3>
        <div className="space-y-3">
          {DAYS.map(({ key, label }) => (
            <div key={key} data-testid={`day-${key}`}
              className="grid grid-cols-12 gap-3 items-center bg-cream rounded-xl p-3 border border-[#E6DEC3]/60">
              <div className="col-span-3 text-brown-dark font-medium">{label}</div>
              <label className="col-span-2 flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!settings.working_hours[key].open}
                  data-testid={`day-${key}-open`}
                  onChange={(e) => updateDay(key, { open: e.target.checked })} />
                <span className="text-xs text-brown-warm">{settings.working_hours[key].open ? "Open" : "Closed"}</span>
              </label>
              <input type="time" value={settings.working_hours[key].open_time}
                data-testid={`day-${key}-open-time`}
                disabled={!settings.working_hours[key].open}
                onChange={(e) => updateDay(key, { open_time: e.target.value })}
                className="col-span-3 bg-cream border border-[#E6DEC3] rounded-full px-3 py-2 text-sm disabled:opacity-40" />
              <input type="time" value={settings.working_hours[key].close_time}
                data-testid={`day-${key}-close-time`}
                disabled={!settings.working_hours[key].open}
                onChange={(e) => updateDay(key, { close_time: e.target.value })}
                className="col-span-3 bg-cream border border-[#E6DEC3] rounded-full px-3 py-2 text-sm disabled:opacity-40" />
              <input type="text" placeholder="Note" value={settings.working_hours[key].note || ""}
                onChange={(e) => updateDay(key, { note: e.target.value })}
                data-testid={`day-${key}-note`}
                className="col-span-12 bg-cream border border-[#E6DEC3] rounded-full px-3 py-2 text-xs" />
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="settings-save">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save Settings
      </button>
    </div>
  );
}

// ----------------- Shared utilities -----------------
function Spinner() {
  return (
    <div className="flex items-center gap-2 text-brown-warm py-6">
      <Loader2 className="animate-spin" size={16} /> Loading…
    </div>
  );
}
function Empty({ message }) {
  return (
    <div className="bg-cream-alt border border-[#E6DEC3] rounded-2xl p-10 text-center text-brown-warm">
      {message}
    </div>
  );
}
function IconBtn({ children, onClick, testId, title, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`h-9 w-9 rounded-full border flex items-center justify-center transition-all ${
        danger
          ? "border-[#E6DEC3] text-brown-warm hover:bg-red-50 hover:text-red-700 hover:border-red-200"
          : "border-[#E6DEC3] text-brown-dark hover:bg-lilac-soft hover:border-lilac"
      }`}
    >
      {children}
    </button>
  );
}
function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-brown-dark/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="modal-overlay">
      <div className="bg-cream rounded-[2rem] border border-[#E6DEC3] p-7 max-w-lg w-full my-12 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl text-brown-dark">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"
            className="h-8 w-8 rounded-full bg-cream-alt hover:bg-lilac-soft text-brown-dark flex items-center justify-center">
            <X size={14} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function ModalInput({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block eyebrow mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-cream-alt border border-[#E6DEC3] rounded-full px-4 py-2.5 text-brown-dark text-sm focus:outline-none focus:ring-1 focus:ring-lilac focus:border-lilac"
      />
    </div>
  );
}
